import {
    BookingStatus,
    ClientStatus,
    ClinicianCredential,
    DateFormats,
    IAvailabilitySchedule,
    IClient,
    IClientBooking,
    IClinician,
    IInsuranceVerificationFields,
    IIntakeAvailability,
    IReservedSchedule,
    RecurrenceRule,
    RequestedServices,
    TRequestedService,
} from "intake_shared/src/types";
import moment from "../utils/app_moment";
import {
    getStringFromScheduleDate,
    findLabelByPracticeId,
    getNumberOfWeeks,
} from "../utils/utils";
import { DepartmentCache } from "../store/cache/requested_service";
import appSettings from "../appSettings";

const mapToThisWeek = (d: moment.Moment) =>
    moment().day(d.day()).hour(d.hour()).minute(d.minute()).second(0).millisecond(0);

const ONE_OFF_AVAILABILITY_SERVICES = [
    "Career Assessment",
    "Evaluation",
    "Mental Health Assessment: Fully In-Person",
    "Mental Health Assessment: Partial In-Person",
    "Mental Health Assessment: Virtual",
    "Mental Health Assessment: Intern",
    "Mental Health Assessment: Extern",
    "Gifted Assessment",
    "Short Term Evaluations & Documentation Services",
    "Ketamine Assisted Psychotherapy (Evaluation)",
    "In-Person Med Management",
    "In-Person Psych Counseling",
    "Medication Management",
    "Psychiatric Counseling",
    "Extern Therapy",
    "Ketamine Assisted Psychotherapy",
    "Nutrition Counseling",
];

export function getAvailabilityRecurrenceOptions(
    availableService: string | undefined,
    requestedServices: TRequestedService,
    allowAnyServiceOneOff = false
) {
    const recurrenceOptions = [
        { value: RecurrenceRule.WEEKLY, label: "Weekly" },
        { value: RecurrenceRule.BIWEEKLY, label: "Bi-Weekly" },
    ];
    const serviceLabel = availableService
        ? requestedServices[availableService] ?? availableService
        : undefined;

    if (
        (availableService === "" && allowAnyServiceOneOff) ||
        (serviceLabel && ONE_OFF_AVAILABILITY_SERVICES.includes(serviceLabel))
    ) {
        recurrenceOptions.push({
            value: RecurrenceRule.ONE_OFF,
            label: "One-Off",
        });
    }

    return recurrenceOptions;
}

export function resolveBookingRequestedService(
    requestedService: string,
    clientRequestedService: string | undefined,
    requestedServices: TRequestedService
): string {
    const service =
        requestedServices[requestedService] ??
        (requestedService || clientRequestedService || "");

    switch (service.trim().toLowerCase()) {
        case "individual":
            return RequestedServices.INDIVIDUAL_THERAPY;
        case "couples":
            return RequestedServices.COUPLES_THERAPY;
        case "psychiatry":
            return RequestedServices.MEDICATION_MANAGEMENT;
        default:
            return service;
    }
}

export function getBookingRequestedServiceDefault(
    clientRequestedService: string | undefined,
    slotAvailableService: string | undefined
): string {
    return clientRequestedService?.trim() || slotAvailableService?.trim() || "";
}

// Returns true when the schedule's start is inside one location's window but its
// end falls inside a window that belongs to a different location/office. This
// distinguishes a cross-location span from a slot that simply extends past the
// end of its own location's coverage.
export function scheduleCrossesLocationBoundary(
    officeWindowSchedules: IAvailabilitySchedule[],
    schedule: IAvailabilitySchedule | IReservedSchedule
): boolean {
    const scheduleStart = mapToThisWeek(
        moment(getStringFromScheduleDate(schedule.start))
    );
    const scheduleEnd = mapToThisWeek(
        moment(getStringFromScheduleDate(schedule.end))
    );

    const startWindow = officeWindowSchedules.find((w) => {
        const wStart = mapToThisWeek(moment(getStringFromScheduleDate(w.start)));
        const wEnd = mapToThisWeek(moment(getStringFromScheduleDate(w.end)));
        return scheduleStart.isSameOrAfter(wStart) && scheduleStart.isBefore(wEnd);
    });

    if (!startWindow) return false;

    return officeWindowSchedules.some((w) => {
        if (w.location === startWindow.location && w.office === startWindow.office) {
            return false;
        }
        const wStart = mapToThisWeek(moment(getStringFromScheduleDate(w.start)));
        const wEnd = mapToThisWeek(moment(getStringFromScheduleDate(w.end)));
        return scheduleEnd.isAfter(wStart) && scheduleEnd.isSameOrBefore(wEnd);
    });
}

export function getMatchingOfficeWindow(
    officeWindowSchedules: IAvailabilitySchedule[],
    schedule: IAvailabilitySchedule | IReservedSchedule
) {
    const scheduleStart = mapToThisWeek(
        moment(getStringFromScheduleDate(schedule.start))
    );
    const scheduleEnd = mapToThisWeek(
        moment(getStringFromScheduleDate(schedule.end))
    );

    // Collect every window that contains the schedule's start time.
    // There may be more than one (e.g. two overlapping windows for different
    // locations that both open at 9 AM). We must try each candidate's
    // location/office group before concluding that no match exists.
    const startCandidates = officeWindowSchedules.filter((w) => {
        const windowStart = mapToThisWeek(
            moment(getStringFromScheduleDate(w.start))
        );
        const windowEnd = mapToThisWeek(moment(getStringFromScheduleDate(w.end)));
        return (
            scheduleStart.isSameOrAfter(windowStart) &&
            scheduleStart.isBefore(windowEnd)
        );
    });

    if (startCandidates.length === 0) {
        return undefined;
    }

    // Deduplicate candidates by location+office so we don't re-run the same
    // group twice when multiple windows from the same location overlap the start.
    const seenGroups = new Set<string>();
    const uniqueCandidates = startCandidates.filter((w) => {
        const key = `${w.location}__${w.office}`;
        if (seenGroups.has(key)) return false;
        seenGroups.add(key);
        return true;
    });

    for (const startingWindow of uniqueCandidates) {
        // Only use windows that share this candidate's location and office to
        // prevent coverage from spanning across different location boundaries.
        const sortedWindows = officeWindowSchedules
            .filter(
                (w) =>
                    w.location === startingWindow.location &&
                    w.office === startingWindow.office
            )
            .map((w) => ({
                start: mapToThisWeek(moment(getStringFromScheduleDate(w.start))),
                end: mapToThisWeek(moment(getStringFromScheduleDate(w.end))),
            }))
            .sort((a, b) => a.start.diff(b.start));

        let currentCoverage = scheduleStart.clone();
        let hasGap = false;

        for (const window of sortedWindows) {
            if (currentCoverage.isSameOrAfter(scheduleEnd)) break;
            if (window.end.isSameOrBefore(currentCoverage)) continue;
            if (window.start.isAfter(currentCoverage)) {
                hasGap = true;
                break;
            }
            currentCoverage = moment.max(currentCoverage, window.end);
        }

        if (!hasGap && currentCoverage.isSameOrAfter(scheduleEnd)) {
            return startingWindow;
        }
    }

    return undefined;
}

export function findAvailabilityConflicts(
    availabilitySchedules: IAvailabilitySchedule[],
    heldBookingSchedules: IAvailabilitySchedule[],
    confirmedBookingSchedules: IAvailabilitySchedule[],
    schedule: IAvailabilitySchedule | IReservedSchedule
) {
    const hasStrictOverlap = (
        aStart: moment.Moment,
        aEnd: moment.Moment,
        bStart: moment.Moment,
        bEnd: moment.Moment
    ) => {
        return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
    };

    let potentialConflicts = [
        ...availabilitySchedules,
        ...heldBookingSchedules,
        ...confirmedBookingSchedules,
    ].filter((a) => {
        const aStart = mapToThisWeek(moment(getStringFromScheduleDate(a.start)));
        const aEnd = mapToThisWeek(moment(getStringFromScheduleDate(a.end)));
        const sStart = mapToThisWeek(
            moment(getStringFromScheduleDate(schedule.start))
        );
        const sEnd = mapToThisWeek(moment(getStringFromScheduleDate(schedule.end)));

        return (
            a.id &&
            (!schedule.id || a.id !== schedule.id) &&
            aStart.day() === sStart.day() &&
            hasStrictOverlap(aStart, aEnd, sStart, sEnd) &&
            // TODO: This should be redone to determine if appointments
            // will be on same day.
            (schedule.recurrenceRule === RecurrenceRule.BIWEEKLY
                ? moment(a.start).format(DateFormats.DAY_MONTH_FORMAT) ===
                  moment(schedule.start).format(DateFormats.DAY_MONTH_FORMAT)
                : true)
        );
    });

    // Check for one-off appointments.
    const scheduleStartDate = schedule.starting_date || schedule.start;
    const scheduleStartDateMoment = moment(scheduleStartDate).startOf("day");
    potentialConflicts = potentialConflicts.filter((c) => {
        const conflictStartDate = c.starting_date || c.start;
        if (
            c.recurrenceRule === RecurrenceRule.ONE_OFF &&
            conflictStartDate &&
            scheduleStartDate
        ) {
            const conflictStartDateMoment = moment(conflictStartDate).startOf("day");
            if (schedule.recurrenceRule === RecurrenceRule.ONE_OFF) {
                return scheduleStartDateMoment.isSame(
                    conflictStartDateMoment,
                    "dates"
                );
            } else {
                return scheduleStartDateMoment.isSameOrBefore(
                    conflictStartDateMoment,
                    "dates"
                );
            }
        }
        return true;
    });

    // check for monthly conflicts
    potentialConflicts = potentialConflicts.filter((c) => {
        const aStart = mapToThisWeek(moment(getStringFromScheduleDate(c.start)));
        const aEnd = mapToThisWeek(moment(getStringFromScheduleDate(c.end)));
        const sStart = mapToThisWeek(
            moment(getStringFromScheduleDate(schedule.start))
        );
        const sEnd = mapToThisWeek(moment(getStringFromScheduleDate(schedule.end)));

        return (
            c.id &&
            (!schedule.id || c.id !== schedule.id) &&
            aStart.day() === sStart.day() &&
            hasStrictOverlap(aStart, aEnd, sStart, sEnd) &&
            (schedule.recurrenceRule === RecurrenceRule.MONTHLY
                ? moment(c.start).format(DateFormats.DAY_MONTH_FORMAT) ===
                  moment(schedule.start).format(DateFormats.DAY_MONTH_FORMAT)
                : true)
        );
    });
    const hasConflicts = potentialConflicts.some(
        (c) => !Boolean((c as IClientBooking).is_paused)
    );
    return hasConflicts;
}

//  Builds a short label for a schedule for conflict messages, e.g. "your Monthly schedule on Sunday" or "your Weekly schedule on Sunday at 12:00pm".
export function getScheduleConflictLabel(
    schedule: IAvailabilitySchedule | IReservedSchedule
): string {
    const start = moment(getStringFromScheduleDate(schedule.start));
    const day = start.format(DateFormats.DAY);
    const time = start.format(DateFormats.HOUR_MIN_FORMAT);
    const rule = schedule.recurrenceRule;
    if (rule === RecurrenceRule.ONE_OFF) {
        return `your one-off on ${day} at ${time}`;
    }
    const ruleLabel = rule ? rule.toLowerCase() : "schedule";
    return `your ${ruleLabel} schedule on ${day} at ${time}`;
}

export interface IntakeSessionConflictWarning {
    hasConflicts: boolean;
    message: string | null;
}

// For intake sessions: same conflict logic as findAvailabilityConflicts but returns a message instead of a boolean. Creation is still allowed when conflicts exist.
export function getIntakeSessionConflictWarning(
    availabilitySchedules: IAvailabilitySchedule[],
    heldBookingSchedules: IAvailabilitySchedule[],
    confirmedBookingSchedules: IAvailabilitySchedule[],
    schedule: IAvailabilitySchedule | IReservedSchedule
): IntakeSessionConflictWarning {
    const hasStrictOverlap = (
        aStart: moment.Moment,
        aEnd: moment.Moment,
        bStart: moment.Moment,
        bEnd: moment.Moment
    ) => {
        return aStart.isBefore(bEnd) && aEnd.isAfter(bStart);
    };

    let potentialConflicts = [
        ...availabilitySchedules,
        ...heldBookingSchedules,
        ...confirmedBookingSchedules,
    ].filter((a) => {
        const aStart = mapToThisWeek(moment(getStringFromScheduleDate(a.start)));
        const aEnd = mapToThisWeek(moment(getStringFromScheduleDate(a.end)));
        const sStart = mapToThisWeek(
            moment(getStringFromScheduleDate(schedule.start))
        );
        const sEnd = mapToThisWeek(moment(getStringFromScheduleDate(schedule.end)));

        return (
            a.id &&
            (!schedule.id || a.id !== schedule.id) &&
            aStart.day() === sStart.day() &&
            hasStrictOverlap(aStart, aEnd, sStart, sEnd) &&
            (schedule.recurrenceRule === RecurrenceRule.BIWEEKLY
                ? moment(a.start).format(DateFormats.DAY_MONTH_FORMAT) ===
                  moment(schedule.start).format(DateFormats.DAY_MONTH_FORMAT)
                : true)
        );
    });

    const scheduleStartDate = schedule.starting_date || schedule.start;
    const scheduleStartDateMoment = moment(scheduleStartDate).startOf("day");
    potentialConflicts = potentialConflicts.filter((c) => {
        const conflictStartDate = c.starting_date || c.start;
        if (
            c.recurrenceRule === RecurrenceRule.ONE_OFF &&
            conflictStartDate &&
            scheduleStartDate
        ) {
            const conflictStartDateMoment = moment(conflictStartDate).startOf("day");
            if (schedule.recurrenceRule === RecurrenceRule.ONE_OFF) {
                return scheduleStartDateMoment.isSame(
                    conflictStartDateMoment,
                    "dates"
                );
            } else {
                return scheduleStartDateMoment.isSameOrBefore(
                    conflictStartDateMoment,
                    "dates"
                );
            }
        }
        return true;
    });

    potentialConflicts = potentialConflicts.filter((c) => {
        const aStart = mapToThisWeek(moment(getStringFromScheduleDate(c.start)));
        const aEnd = mapToThisWeek(moment(getStringFromScheduleDate(c.end)));
        const sStart = mapToThisWeek(
            moment(getStringFromScheduleDate(schedule.start))
        );
        const sEnd = mapToThisWeek(moment(getStringFromScheduleDate(schedule.end)));

        return (
            c.id &&
            (!schedule.id || c.id !== schedule.id) &&
            aStart.day() === sStart.day() &&
            hasStrictOverlap(aStart, aEnd, sStart, sEnd) &&
            (schedule.recurrenceRule === RecurrenceRule.MONTHLY
                ? moment(c.start).format(DateFormats.DAY_MONTH_FORMAT) ===
                  moment(schedule.start).format(DateFormats.DAY_MONTH_FORMAT)
                : true)
        );
    });

    const conflictsExcludingPaused = potentialConflicts.filter(
        (c) => !Boolean((c as IClientBooking).is_paused)
    );
    if (conflictsExcludingPaused.length === 0) {
        return { hasConflicts: false, message: null };
    }

    const labels = conflictsExcludingPaused.map((c) =>
        getScheduleConflictLabel(c as IAvailabilitySchedule)
    );
    const scheduleList =
        labels.length === 1
            ? labels[0]
            : labels.slice(0, -1).join(", ") + " and " + labels[labels.length - 1];
    const message = `This time you are about to set conflicts with ${scheduleList}, however you can go ahead to create it.`;
    return { hasConflicts: true, message };
}

// Therapy services that are eligible for intake sessions
export const ELIGIBLE_THERAPY_SERVICES = [
    "Child Therapy",
    "Couples Therapy",
    "Extern Therapy",
    "Family Therapy",
    "In-Person Therapy",
    "Individual Therapy",
    "Teletherapy",
    "Intuitive Eating",
    "Ketamine Assisted Psychotherapy",
    "Ketamine Assisted Psychotherapy (Evaluation)",
    "Ketamine Assisted Psychotherapy (Therapy Intake)",
];

const eligibleCredentials = [ClinicianCredential.MHCLP, ClinicianCredential.LMSW];

export function meetsIntakeSessionClientAndBookingRequirements(
    client: IClient,
    booking: IClientBooking
): boolean {
    if (client.insurance_provider?.toLowerCase() === "self-pay") {
        return false;
    }

    const feeSetting = client[ClientStatus.FEE_SETTING];
    if (feeSetting && (feeSetting as any).is_self_pay === true) {
        return false;
    }

    if (!ELIGIBLE_THERAPY_SERVICES.includes(booking.requested_service)) {
        return false;
    }

    return true;
}

// Check if a client/clinician/booking combination is eligible for intake session
export function isEligibleForIntakeSession(
    client: IClient,
    clinician: IClinician,
    booking: IClientBooking
): boolean {
    if (!meetsIntakeSessionClientAndBookingRequirements(client, booking)) {
        return false;
    }

    if (!eligibleCredentials.includes(clinician.credential)) {
        return false;
    }

    return true;
}

// Get list of therapy services eligible for intake sessions
export function getTherapyServices(): string[] {
    return [...ELIGIBLE_THERAPY_SERVICES];
}

// Group intake availability slots by day
export function formatIntakeAvailabilitySlots(slots: IIntakeAvailability[]): {
    [day: string]: IIntakeAvailability[];
} {
    const grouped: { [day: string]: IIntakeAvailability[] } = {};
    slots.forEach((slot) => {
        const dayKey = moment(slot.start_time).format("YYYY-MM-DD");
        if (!grouped[dayKey]) {
            grouped[dayKey] = [];
        }
        grouped[dayKey].push(slot);
    });
    return grouped;
}

// Check if an intake session is completed
export function isIntakeSessionCompleted(
    intakeSession?: IClientBooking["supervisor_intake_session"]
): boolean {
    return Boolean(intakeSession?.completed_at);
}

// Get intake session status (scheduled or completed)
export function getIntakeSessionStatus(
    intakeSession?: IClientBooking["supervisor_intake_session"]
): "scheduled" | "completed" {
    return isIntakeSessionCompleted(intakeSession) ? "completed" : "scheduled";
}

export function getIntakeStatusBadgeProps(status?: string): {
    bgColor: string;
    label: string;
} {
    const normalizedStatus = status ? String(status).toLowerCase() : "available";

    let bgColor = "#E53935";
    let label = "available";

    if (normalizedStatus === "held") {
        bgColor = "#FF9800";
        label = "held";
    } else if (normalizedStatus === "booked" || normalizedStatus === "confirmed") {
        bgColor = "green";
        label = "booked";
    }

    return { bgColor, label };
}

export const safeToDate = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date) return value;
    if (typeof value.toDate === "function") {
        return value.toDate();
    }

    return null;
};

// Get enriched department labels for a supervisor
export function getSupervisorDepartmentLabels(
    supervisor: IClinician,
    allDepartmentsAndServices: DepartmentCache
): string {
    const departmentLabels = (supervisor.departments || [])
        .map((deptId) => {
            const label = findLabelByPracticeId(deptId, allDepartmentsAndServices);
            return label || deptId;
        })
        .filter(Boolean);

    // Special case: add PH for specific supervisors if not already present
    if (
        supervisor.email &&
        appSettings.SUPERVISORS_WITH_PH_DEPARTMENT.includes(supervisor.email) &&
        !departmentLabels.includes("PH")
    ) {
        departmentLabels.push("PH");
    }

    return departmentLabels.length > 0 ? departmentLabels.join(", ") : "";
}

// Load and enrich supervisors with intake availability
export async function loadSupervisorsWithIntake(
    clinician: IClinician,
    allDepartmentsAndServices: DepartmentCache,
    fetchAllSupervisors: () => Promise<IClinician[]>
): Promise<{
    enrichedSupervisors: Array<
        IClinician & {
            supervisorRole?: "primary" | "secondary" | "not_current_supervisor";
            supervisorPracticeLabel?: string;
        }
    >;
    availability: { [key: string]: IIntakeAvailability[] };
}> {
    const allSupervisors = await fetchAllSupervisors();
    const now = moment();

    const availability: { [key: string]: IIntakeAvailability[] } = {};
    const supervisorsWithIntake: IClinician[] = [];

    for (const sup of allSupervisors) {
        const validSlots = (sup.available_for_intake_sessions || []).filter(
            (slot) => {
                if (!slot.start_time) {
                    return false;
                }

                const slotStartTime = moment(slot.start_time);
                if (!slotStartTime.isValid() || !slotStartTime.isAfter(now)) {
                    return false;
                }

                if (slot.booking_id || slot.client_id || slot.client_display_name) {
                    return false;
                }

                return true;
            }
        );

        if (validSlots.length > 0) {
            availability[sup.email] = validSlots;
            supervisorsWithIntake.push(sup);
        }
    }

    const primarySupervisorId = clinician.supervisor_id;
    const secondaryPairs = clinician.secondary_supervisor_ids || [];

    const enrichedSupervisors = supervisorsWithIntake.map((sup) => {
        let supervisorRole:
            | "primary"
            | "secondary"
            | "not_current_supervisor"
            | undefined;
        let supervisorPracticeLabel: string | undefined;

        if (
            primarySupervisorId &&
            (sup.id === primarySupervisorId || sup.email === primarySupervisorId)
        ) {
            supervisorRole = "primary";
            supervisorPracticeLabel = clinician.departments?.[0] || "";
        } else {
            const secondaryPair = secondaryPairs.find((p) => {
                const supId = sup.id || "";
                const supEmail = sup.email || "";
                return p.supervisor_id === supId || p.supervisor_id === supEmail;
            });

            if (secondaryPair) {
                supervisorRole = "secondary";
                const label = findLabelByPracticeId(
                    secondaryPair.practice_id,
                    allDepartmentsAndServices
                );
                supervisorPracticeLabel = label || "";
            } else {
                supervisorRole = "not_current_supervisor";
                supervisorPracticeLabel = getSupervisorDepartmentLabels(
                    sup,
                    allDepartmentsAndServices
                );
            }
        }

        return {
            ...sup,
            supervisorRole,
            supervisorPracticeLabel,
        } as IClinician & {
            supervisorRole?: "primary" | "secondary" | "not_current_supervisor";
            supervisorPracticeLabel?: string;
        };
    });

    return {
        enrichedSupervisors,
        availability,
    };
}

type SelfPayInsurancePatch = Pick<
    IInsuranceVerificationFields,
    | "effective_date"
    | "policy_effective_date"
    | "last_verification_date"
    | "accumulations_cal_yr"
>;

export function getSelfPayInsurancePatch(
    client: IClient,
    now: Date = new Date()
): Partial<SelfPayInsurancePatch> {
    const insuranceVerification = client[ClientStatus.INS_VERIFICATION];
    const today = moment(now).format(DateFormats.DAY_MONTH_YEAR_FORMAT);
    const patch: Partial<SelfPayInsurancePatch> = {};

    if (!insuranceVerification?.effective_date) {
        patch.effective_date = today;
    }
    if (!insuranceVerification?.policy_effective_date) {
        patch.policy_effective_date = today;
    }
    if (!insuranceVerification?.last_verification_date) {
        patch.last_verification_date = now;
    }
    if (!insuranceVerification?.accumulations_cal_yr) {
        patch.accumulations_cal_yr = "Y";
    }

    return patch;
}

const SELF_PAY_FIELD_LABELS: Record<keyof SelfPayInsurancePatch, string> = {
    effective_date: "effective date",
    policy_effective_date: "policy date",
    last_verification_date: "last verified date",
    accumulations_cal_yr: "accumulations",
};

export function buildSelfPayAutoFillToastMessage(
    patch: Partial<SelfPayInsurancePatch>
): string | null {
    const filledKeys = Object.keys(patch) as Array<keyof SelfPayInsurancePatch>;
    if (filledKeys.length === 0) {
        return null;
    }
    const labels = filledKeys.map((key) => SELF_PAY_FIELD_LABELS[key]);
    const formatted =
        labels.length === 1
            ? labels[0]
            : labels.length === 2
            ? `${labels[0]} and ${labels[1]}`
            : `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
    return `Auto-filled ${formatted} for self-pay.`;
}

export function getClinicianCaseloadStats(
    clinician: Pick<
        IClinician,
        "confirmed" | "held" | "available_for_intake_sessions" | "ideal_caseload"
    >
) {
    const clients = new Set<string>();
    const heldClients = new Set<string>();
    const entities: Record<
        string,
        { clients: Set<string>; weeklySessions: number }
    > = {};
    const frequencies: Record<
        string,
        {
            recurrence: string;
            bookings: number;
            weight: number;
            weeklySessions: number;
        }
    > = {};
    const bookingIds = new Set<string>();
    let weeklySessions = 0;

    for (const booking of [
        ...(clinician.confirmed ?? []),
        ...(clinician.held ?? []),
    ]) {
        if (!booking.id || !booking.client_id || bookingIds.has(booking.id)) {
            continue;
        }
        bookingIds.add(booking.id);
        if (booking.status === BookingStatus.HELD) {
            heldClients.add(booking.client_id);
            continue;
        }
        if (booking.status !== BookingStatus.CONFIRMED) {
            continue;
        }
        const practiceId =
            booking.practice_id || process.env.REACT_APP_MANTLE_PRACTICE_ID || "";
        const entity = entities[practiceId] ?? {
            clients: new Set<string>(),
            weeklySessions: 0,
        };
        const weeks = getNumberOfWeeks(booking.recurrenceRule);
        const sessions = weeks > 0 ? 1 / weeks : 0;
        if (weeks > 0) {
            const recurrence = booking.recurrenceRule ?? RecurrenceRule.WEEKLY;
            const frequency = frequencies[recurrence] ?? {
                recurrence,
                bookings: 0,
                weight: sessions,
                weeklySessions: 0,
            };
            frequency.bookings += 1;
            frequency.weeklySessions += sessions;
            frequencies[recurrence] = frequency;
        }
        clients.add(booking.client_id);
        entity.clients.add(booking.client_id);
        entity.weeklySessions += sessions;
        entities[practiceId] = entity;
        weeklySessions += sessions;
    }

    for (const session of clinician.available_for_intake_sessions ?? []) {
        if (!session.booking_id || !session.client_id) {
            continue;
        }
        if (session.status === "held") {
            heldClients.add(session.client_id);
        } else if (session.status === "confirmed") {
            const practiceId =
                session.practice_id ||
                process.env.REACT_APP_MANTLE_PRACTICE_ID ||
                "";
            const entity = entities[practiceId] ?? {
                clients: new Set<string>(),
                weeklySessions: 0,
            };
            clients.add(session.client_id);
            entity.clients.add(session.client_id);
            entities[practiceId] = entity;
        }
    }

    const idealCaseload = clinician.ideal_caseload;
    return {
        clients: clients.size,
        heldClients: heldClients.size,
        weeklySessions,
        frequencies: Object.values(frequencies).sort((a, b) => b.weight - a.weight),
        idealCaseload,
        percentFull:
            idealCaseload != null &&
            Number.isFinite(idealCaseload) &&
            idealCaseload > 0
                ? Math.round((weeklySessions / idealCaseload) * 100)
                : null,
        entities: Object.entries(entities).map(([practiceId, entity]) => ({
            practiceId,
            clients: entity.clients.size,
            weeklySessions: entity.weeklySessions,
        })),
    };
}
